import { useId, useRef } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'

/** A tab's optional trailing badge — a numeric count (e.g. "Parts 38" —
 * components.md "Tabs") OR a non-numeric warning marker (an orange circular
 * "!", e.g. the order-detail Customs information tab,
 * `Fictiv Platform.dc.html:3342`). Modeled as a discriminated union (rather
 * than two independent optional props) so a tab can never carry both badge
 * variants at once — the type itself enforces the mutual exclusivity that
 * used to only hold "in practice". */
export type TabBadge =
  | { readonly kind: 'count'; readonly count: number }
  | { readonly kind: 'warning' }

export interface TabItem {
  readonly id: string
  readonly label: string
  readonly badge?: TabBadge
}

export interface TabsProps {
  readonly items: readonly TabItem[]
  readonly activeId: string
  readonly onChange: (id: string) => void
  readonly children: (activeId: string) => ReactNode
  readonly 'aria-label': string
  /** UID-289 (ADR-0065 amendment) — an optional CSS-skin hook on the root
   * element (e.g. `"tabs-segmented"`, ui.css) so a specific consumer can
   * opt into a different visual treatment of this SAME primitive (tablist/
   * roving-tabindex/arrow-key semantics unchanged) without repainting the
   * shared `.tabs-list`/`.tab`/`.tab-active` rules every other consumer
   * renders through. Omit for the existing underline style. */
  readonly className?: string
}

/**
 * Horizontal tabs with an underline active-indicator (components.md "Tabs").
 * The source prototype uses plain `<span>` triggers with no ARIA roles —
 * this spec intentionally upgrades that to a proper `role="tablist"`/
 * `"tab"`/`"tabpanel"` structure with roving-tabindex arrow-key navigation
 * (Left/Right/Home/End), per the accessibility-wcag skill and
 * components.md's explicit fidelity-vs-a11y deviation note. QA should
 * expect this structural difference from the raw HTML prototype — it's
 * correct, not a regression (open-questions.md #13).
 */
export function Tabs({ items, activeId, onChange, children, className, ...rest }: TabsProps) {
  const baseId = useId()
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  function focusAndSelect(id: string) {
    onChange(id)
    tabRefs.current[id]?.focus()
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault()
      const delta = event.key === 'ArrowRight' ? 1 : -1
      const next = items[(index + delta + items.length) % items.length]
      if (next) focusAndSelect(next.id)
    } else if (event.key === 'Home') {
      event.preventDefault()
      const first = items[0]
      if (first) focusAndSelect(first.id)
    } else if (event.key === 'End') {
      event.preventDefault()
      const last = items[items.length - 1]
      if (last) focusAndSelect(last.id)
    }
  }

  return (
    <div className={className ? `tabs ${className}` : 'tabs'}>
      <div role="tablist" aria-label={rest['aria-label']} className="tabs-list">
        {items.map((item, index) => {
          const selected = item.id === activeId
          return (
            <button
              key={item.id}
              ref={(el) => {
                tabRefs.current[item.id] = el
              }}
              role="tab"
              type="button"
              id={`${baseId}-tab-${item.id}`}
              aria-controls={`${baseId}-panel-${item.id}`}
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              className={selected ? 'tab tab-active' : 'tab'}
              onClick={() => onChange(item.id)}
              onKeyDown={(event) => handleKeyDown(event, index)}
            >
              {item.label}
              {item.badge?.kind === 'count' && (
                <span className="tab-count">{item.badge.count}</span>
              )}
              {item.badge?.kind === 'warning' && (
                <>
                  <span className="tab-badge-warning" aria-hidden="true">
                    !
                  </span>
                  <span className="sr-only"> — attention needed</span>
                </>
              )}
            </button>
          )
        })}
      </div>
      {items.map((item) => (
        <div
          key={item.id}
          role="tabpanel"
          id={`${baseId}-panel-${item.id}`}
          aria-labelledby={`${baseId}-tab-${item.id}`}
          hidden={item.id !== activeId}
        >
          {item.id === activeId && children(activeId)}
        </div>
      ))}
    </div>
  )
}
